/**
 * Google Analytics 4 (GA4) helper functions for Vite + React
 * Provides safe client-side analytics tracking
 * 
 * Environment variable required:
 * - VITE_GA_MEASUREMENT_ID: Your GA4 Measurement ID (e.g., G-XXXXXXXXXX)
 *   Set this in Vercel: Project Settings > Environment Variables
 *   (Set for Production + Preview environments)
 */

export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

let gaInitialized = false;

/**
 * Initialize Google Analytics
 * Idempotent - safe to call multiple times
 */
export function initGA() {
  if (typeof window === 'undefined') return;
  if (!GA_MEASUREMENT_ID) return;
  if (gaInitialized) return;

  // Dev-only logging
  if (import.meta.env.DEV) {
    console.log('[Analytics] Initializing GA4 with ID:', GA_MEASUREMENT_ID);
  }

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;
  window.gtag('js', new Date());

  // Load gtag.js script if not already loaded
  if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  // Configure GA4 with send_page_view: false (we'll fire pageviews manually)
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  gaInitialized = true;
}

/**
 * Track a pageview
 * @param {string} path - The URL path to track (e.g., '/dashboard', '/profile/username')
 */
export function pageview(path) {
  if (typeof window === 'undefined') return;
  if (!window.gtag) {
    // If gtag isn't ready, try to init
    initGA();
    if (!window.gtag) return;
  }
  if (!GA_MEASUREMENT_ID) return;

  const fullPath = path || window.location.pathname;
  const fullUrl = window.location.origin + fullPath;

  // Dev-only logging
  if (import.meta.env.DEV) {
    console.log('[Analytics] pageview:', fullPath);
  }

  // Fire page_view event
  window.gtag('event', 'page_view', {
    page_location: fullUrl,
    page_path: fullPath,
  });
}

/**
 * Track a custom event
 * @param {string} name - Event name (e.g., 'signup_started', 'profile_viewed')
 * @param {Record<string, any>} [params] - Optional event parameters (no PII allowed)
 */
export function event(name, params) {
  if (typeof window === 'undefined') return;
  if (!window.gtag) {
    // If gtag isn't ready, try to init
    initGA();
    if (!window.gtag) return;
  }
  if (!GA_MEASUREMENT_ID) return;

  // Dev-only logging
  if (import.meta.env.DEV) {
    console.log('[Analytics] event:', name, params || {});
  }

  window.gtag('event', name, params || {});
}

